/**
 * SSP (Server Side Proxy) admin tab interactions.
 *
 * Handles activate, enable/disable toggle, disconnect,
 * sync, test connection, and proxy failure behavior setting.
 *
 * @since 1.57.0
 */

jQuery(function ($) {

	const $activateBtn    = $("#pmw-ssp-activate-btn")
	const $reactivateBtn  = $("#pmw-ssp-reactivate-btn")
	const $disableBtn     = $("#pmw-ssp-disable-btn")
	const $disconnectBtn  = $("#pmw-ssp-disconnect-btn")
	const $syncBtn        = $("#pmw-ssp-sync-btn")
	const $testBtn        = $("#pmw-ssp-test-btn")
	const $tokenInput     = $("#pmw-ssp-sync-token")
	const $replaceBtn     = $("#pmw-ssp-replace-btn")
	const $replaceRow     = $("#pmw-ssp-replace-row")
	const $replaceInput   = $("#pmw-ssp-replace-input")
	const $replaceSaveBtn = $("#pmw-ssp-replace-save-btn")
	const $replaceCancelBtn = $("#pmw-ssp-replace-cancel-btn")
	const $tokenRow       = $("#pmw-ssp-token-row")
	const $failureBehavior = $("#pmw-ssp-failure-behavior")
	const $diagnosticsBtn = $("#pmw-ssp-diagnostics-btn")

	// If no SSP elements exist on the page, bail early
	if (!$activateBtn.length && !$syncBtn.length) return

	const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
		c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"
	))

	const showStatus = ($el, message, type, errorMeta) => {
		const className = type === "success"
			? "pmw-ssp-msg-success"
			: type === "error"
				? "pmw-ssp-msg-error"
				: "pmw-ssp-msg-loading"

		let html = `<span class="${className}">${escapeHtml(message)}`

		if (type === "error" && errorMeta && (errorMeta.ssp_error_code || errorMeta.ssp_request_id || errorMeta.http_status)) {
			const detailParts = []
			if (errorMeta.ssp_error_code) {
				detailParts.push(`code: <code>${escapeHtml(errorMeta.ssp_error_code)}</code>`)
			}
			if (errorMeta.http_status) {
				detailParts.push(`HTTP ${escapeHtml(String(errorMeta.http_status))}`)
			}
			if (errorMeta.ssp_request_id) {
				detailParts.push(`request id: <code>${escapeHtml(errorMeta.ssp_request_id)}</code>`)
			}
			if (detailParts.length) {
				html += `<br><small class="pmw-ssp-error-meta">${detailParts.join(" · ")}</small>`
			}
			html += `<br><a href="#" class="pmw-ssp-copy-diagnostics-link">Copy diagnostic info for support</a>`
		}

		html += `</span>`
		$el.html(html)
	}

	const clearStatus = ($el) => {
		$el.html("")
	}

	// Parse WP REST error envelopes consistently. WordPress serializes WP_Error as
	// { code, message, data: { status, ... } } and the response wrapper may also
	// nest these under data. Extract the SSP fields we attached server-side.
	const extractErrorMeta = (data) => {
		if (!data) return null
		const meta = (data.data && typeof data.data === "object") ? data.data : data
		return {
			ssp_error_code: meta.ssp_error_code || "",
			ssp_request_id: meta.ssp_request_id || "",
			http_status:    meta.http_status || meta.status || 0,
		}
	}

	const copyToClipboard = (text) => {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			return navigator.clipboard.writeText(text)
		}
		// Fallback for older browsers / non-HTTPS contexts
		return new Promise((resolve, reject) => {
			try {
				const ta = document.createElement("textarea")
				ta.value = text
				ta.style.position = "fixed"
				ta.style.opacity = "0"
				document.body.appendChild(ta)
				ta.select()
				document.execCommand("copy")
				document.body.removeChild(ta)
				resolve()
			} catch (e) {
				reject(e)
			}
		})
	}

	const fetchAndCopyDiagnostics = ($statusEl) => {
		if ($statusEl) showStatus($statusEl, "Building diagnostic snapshot…", "loading")
		return apiCall("diagnostics", "GET")
			.then(data => {
				const text = data && data.diagnostics && data.diagnostics.text
				if (!text) {
					throw new Error("Diagnostics response was empty.")
				}
				return copyToClipboard(text).then(() => {
					if ($statusEl) showStatus($statusEl, "✓ Diagnostic info copied to clipboard — paste it into your support ticket.", "success")
				})
			})
			.catch(err => {
				if ($statusEl) showStatus($statusEl, "Could not copy diagnostics: " + formatError(err), "error")
			})
	}

	// Delegate Copy-from-error-link clicks (these elements are rendered on demand).
	$(document).on("click", ".pmw-ssp-copy-diagnostics-link", function (e) {
		e.preventDefault()
		const $status = $(this).closest("span").parent()
		fetchAndCopyDiagnostics($status)
	})

	const apiCall = (endpoint, method, body) => {
		const opts = {
			method,
			credentials: "same-origin",
			headers: {
				"Content-Type": "application/json",
				"X-WP-Nonce": pmwAdminApi.nonce,
			},
		}

		if (body) {
			opts.body = JSON.stringify(body)
		}

		return fetch(pmwAdminApi.root + "pmw/v1/ssp/" + endpoint, opts)
			.then(response => {
				const contentType = response.headers.get("content-type") || ""
				if (!contentType.includes("application/json")) {
					throw new Error(
						"The server returned an unexpected response (HTTP " + response.status + "). " +
						"This is usually caused by a security plugin, firewall, or a PHP error. " +
						"Check the server error logs for details."
					)
				}
				return response.json()
			})
	}

	const formatError = (err) => {
		if (err instanceof TypeError) {
			return "Connection error: Could not reach the server. Check your internet connection and try again."
		}
		return err.message || "An unknown error occurred."
	}

	// ─── Activate ──────────────────────────────────────────

	$activateBtn.on("click", function () {
		const token = $.trim($tokenInput.val())

		if (!token) {
			showStatus($("#pmw-ssp-activate-status"), "Please enter a sync token.", "error")
			return
		}

		// Basic hex validation
		if (!/^[a-f0-9]{32,64}$/i.test(token)) {
			showStatus($("#pmw-ssp-activate-status"), "Invalid token format. Expected a hex string (32-64 characters).", "error")
			return
		}

		$activateBtn.prop("disabled", true).text("Activating…")
		clearStatus($("#pmw-ssp-activate-status"))

		apiCall("activate", "POST", { sync_token: token })
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-activate-status"), "✓ SSP activated and destinations synced successfully. Reloading…", "success")
					setTimeout(() => window.location.reload(), 1500)
				} else {
					const msg = data.message || data.data?.message || "Activation failed."
					showStatus($("#pmw-ssp-activate-status"), msg, "error", extractErrorMeta(data))
					$activateBtn.prop("disabled", false).text("Activate")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-activate-status"), formatError(err), "error")
				$activateBtn.prop("disabled", false).text("Activate")
			})
	})

	// ─── Enable / Disable Buttons ─────────────────────────

	$reactivateBtn.on("click", function () {
		$reactivateBtn.prop("disabled", true).text("Re-activating…")
		showStatus($("#pmw-ssp-toggle-status"), "Re-activating…", "loading")

		apiCall("enable", "POST")
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-toggle-status"), "✓ " + data.message + " Reloading…", "success")
					setTimeout(() => window.location.reload(), 1500)
				} else {
					const msg = data.message || data.data?.message || "Re-activation failed."
					showStatus($("#pmw-ssp-toggle-status"), msg, "error", extractErrorMeta(data))
					$reactivateBtn.prop("disabled", false).text("Re-activate")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-toggle-status"), formatError(err), "error")
				$reactivateBtn.prop("disabled", false).text("Re-activate")
			})
	})

	$disableBtn.on("click", function () {
		$disableBtn.prop("disabled", true).text("Disabling…")
		showStatus($("#pmw-ssp-toggle-status"), "Disabling…", "loading")

		apiCall("disable", "POST")
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-toggle-status"), "✓ " + data.message + " Reloading…", "success")
					setTimeout(() => window.location.reload(), 1500)
				} else {
					const msg = data.message || data.data?.message || "Disable failed."
					showStatus($("#pmw-ssp-toggle-status"), msg, "error")
					$disableBtn.prop("disabled", false).text("Disable")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-toggle-status"), formatError(err), "error")
				$disableBtn.prop("disabled", false).text("Disable")
			})
	})

	// ─── Replace Token ─────────────────────────────────────
	//
	// The masked "current token" input is intentionally disabled so users
	// cannot paste into it expecting the value to be saved. Re-activate
	// only flips the enabled flag on the stored token, so a paste followed
	// by Re-activate would silently keep the OLD token (this exact mistake
	// stranded a customer with a stale token that no longer authenticated
	// against SSP). To rotate the token in place we hide the masked row,
	// show an editable input, and POST the new value to /activate (which
	// overwrites the stored token after SSP validates it).

	$replaceBtn.on("click", function () {
		$tokenRow.hide()
		$replaceRow.css("display", "flex")
		$replaceInput.val("").trigger("focus")
		clearStatus($("#pmw-ssp-activate-status"))
		clearStatus($("#pmw-ssp-toggle-status"))
	})

	$replaceCancelBtn.on("click", function () {
		$replaceRow.hide()
		$replaceInput.val("")
		$tokenRow.css("display", "flex")
	})

	$replaceSaveBtn.on("click", function () {
		const token = $replaceInput.val().trim()

		if (!token) {
			showStatus($("#pmw-ssp-activate-status"), "Please paste the new sync token.", "error")
			return
		}
		if (!/^[a-f0-9]{32,64}$/i.test(token)) {
			showStatus($("#pmw-ssp-activate-status"), "Invalid token format. Expected a hex string (32-64 characters).", "error")
			return
		}

		$replaceSaveBtn.prop("disabled", true).text("Saving…")
		$replaceCancelBtn.prop("disabled", true)
		clearStatus($("#pmw-ssp-activate-status"))

		apiCall("activate", "POST", { sync_token: token })
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-activate-status"), "✓ Token replaced and SSP re-activated. Reloading…", "success")
					setTimeout(() => window.location.reload(), 1500)
				} else {
					const msg = data.message || data.data?.message || "Token replacement failed."
					showStatus($("#pmw-ssp-activate-status"), msg, "error", extractErrorMeta(data))
					$replaceSaveBtn.prop("disabled", false).text("Save new token")
					$replaceCancelBtn.prop("disabled", false)
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-activate-status"), formatError(err), "error")
				$replaceSaveBtn.prop("disabled", false).text("Save new token")
				$replaceCancelBtn.prop("disabled", false)
			})
	})

	// ─── Disconnect ────────────────────────────────────────

	$disconnectBtn.on("click", function () {
		if (!confirm(
			"Disconnect from SSP?\n\n" +
			"This will permanently delete the sync token and remove all connection data. " +
			"You will need to re-enter the token from the SSP portal to reconnect.\n\n" +
			"Continue?"
		)) {
			return
		}

		$disconnectBtn.prop("disabled", true).text("Disconnecting…")

		apiCall("disconnect", "POST")
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-disconnect-status"), "SSP disconnected. Reloading…", "success")
					setTimeout(() => window.location.reload(), 1500)
				} else {
					showStatus($("#pmw-ssp-disconnect-status"), data.message || "Disconnect failed.", "error")
					$disconnectBtn.prop("disabled", false).text("Disconnect")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-disconnect-status"), formatError(err), "error")
				$disconnectBtn.prop("disabled", false).text("Disconnect")
			})
	})

	// ─── Proxy Failure Behavior ────────────────────────────

	$failureBehavior.on("change", function () {
		const value = $(this).val()

		showStatus($("#pmw-ssp-failure-behavior-status"), "Saving…", "loading")

		apiCall("save-setting", "POST", { key: "proxy_failure_behavior", value })
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-failure-behavior-status"), "✓ Saved", "success")
					setTimeout(() => clearStatus($("#pmw-ssp-failure-behavior-status")), 3000)
				} else {
					showStatus($("#pmw-ssp-failure-behavior-status"), data.message || "Save failed.", "error")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-failure-behavior-status"), formatError(err), "error")
			})
	})

	/**
	 * Update plan and usage status fields from a sync response.
	 */
	const updateSyncMeta = (status) => {
		if (!status) return

		if (status.last_sync_at_formatted) {
			$("#pmw-ssp-last-sync").text(status.last_sync_at_formatted)
		}

		if (status.plan_name) {
			let planHtml = status.plan_name
			if (status.subscription_status && status.subscription_status !== "active") {
				planHtml += ` <span class="pmw-ssp-badge pmw-ssp-badge--inactive">${status.subscription_status.charAt(0).toUpperCase() + status.subscription_status.slice(1)}</span>`
			}
			$("#pmw-ssp-plan-name").html(planHtml)
		}

		if (typeof status.monthly_request_limit !== "undefined" && status.monthly_request_limit > 0) {
			const fmt = (n) => n.toLocaleString()
			$("#pmw-ssp-usage").text(
				`${fmt(status.billable_this_period)} / ${fmt(status.monthly_request_limit)} events (${status.usage_percent}%)`
			)
		}
	}

	// ─── Sync Now ──────────────────────────────────────────

	$syncBtn.on("click", function () {
		$syncBtn.prop("disabled", true).text("Syncing…")
		showStatus($("#pmw-ssp-action-status"), "Pushing configuration…", "loading")

		apiCall("sync", "POST")
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-action-status"), "✓ Config synced successfully. Reloading…", "success")
					updateSyncMeta(data.status)
					setTimeout(() => window.location.reload(), 1500)
				} else {
					showStatus($("#pmw-ssp-action-status"), data.message || data.data?.message || "Sync failed.", "error", extractErrorMeta(data))
					$syncBtn.prop("disabled", false).text("Sync Now")
				}
			})
			.catch(err => {
				showStatus($("#pmw-ssp-action-status"), formatError(err), "error")
				$syncBtn.prop("disabled", false).text("Sync Now")
			})
	})

	// ─── Copy Diagnostics for Support ──────────────────────

	$diagnosticsBtn.on("click", function () {
		const $status = $("#pmw-ssp-diagnostics-status")
		fetchAndCopyDiagnostics($status)
	})

	// ─── Test Connection ───────────────────────────────────

	$testBtn.on("click", function () {
		$testBtn.prop("disabled", true).text("Testing…")
		showStatus($("#pmw-ssp-action-status"), "Testing proxy connectivity…", "loading")

		apiCall("test", "POST")
			.then(data => {
				if (data.success) {
					showStatus($("#pmw-ssp-action-status"), "✓ " + data.message, "success")
				} else {
					showStatus($("#pmw-ssp-action-status"), data.message || "Connection test failed.", "error")
				}
				$testBtn.prop("disabled", false).text("Test Connection")
			})
			.catch(err => {
				showStatus($("#pmw-ssp-action-status"), formatError(err), "error")
				$testBtn.prop("disabled", false).text("Test Connection")
			})
	})
})
